import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { IS_AUTHENTICATED_KEY } from '~/modules/auth/constants.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import type { RootState } from '~/store/store.ts';
import {
  selectGuaranteesAdministrationSupervisorInfoStatus,
  selectHasGuaranteesAdministrationAccess,
} from '~/store/guarantees/selectors.ts';
import { fetchGuaranteesAdministrationSupervisorInfo } from '~/store/guarantees/thunks/applicationThunks.ts';
import { GuaranteesFetchStatus } from '~/store/guarantees/types.ts';
import { supervisorAdministrationPageConfigs } from './config.ts';
import type { TSupervisorAdministrationPageKey } from './types.ts';

const resolvePageKey = (pathname: string): TSupervisorAdministrationPageKey | undefined =>
  (Object.keys(supervisorAdministrationPageConfigs) as TSupervisorAdministrationPageKey[]).find(
    (pageKey) => supervisorAdministrationPageConfigs[pageKey].path === pathname
  );

const ProtectedSupervisorAdministrationRoute = () => {
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const { token, loading, isInitialized } = useSelector((state: RootState) => state.users);
  const pageKey = useMemo(() => resolvePageKey(pathname), [pathname]);
  const status = useSelector((state: RootState) =>
    pageKey
      ? selectGuaranteesAdministrationSupervisorInfoStatus(state, pageKey)
      : GuaranteesFetchStatus.Error
  );
  const hasAccess = useSelector((state: RootState) =>
    pageKey ? selectHasGuaranteesAdministrationAccess(state, pageKey) : false
  );
  const isAuthorized = localStorage.getItem(IS_AUTHENTICATED_KEY);

  useEffect(() => {
    if (token && pageKey && status === GuaranteesFetchStatus.Idle) {
      void dispatch(fetchGuaranteesAdministrationSupervisorInfo({ pageKey }));
    }
  }, [dispatch, pageKey, status, token]);

  if ((!isInitialized || (loading && !token)) && isAuthorized) return <FullPageLoader />;
  if (
    !token ||
    !pageKey ||
    status === GuaranteesFetchStatus.Error ||
    (status === GuaranteesFetchStatus.Success && !hasAccess)
  ) {
    return <Navigate to="/manager/sign-in" state={{ from: `${pathname}${search}` }} replace />;
  }
  if (status === GuaranteesFetchStatus.Idle || status === GuaranteesFetchStatus.Loading) {
    return <FullPageLoader />;
  }

  return <Outlet />;
};

export default ProtectedSupervisorAdministrationRoute;
